import { Modal, Button, Col, Row, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { metersToKms } from '../../utils/distanceUtils'
import styles from './styles.module.css'
import { DeliveryFlow } from '../Delivery/DeliveryFlow'
import { useState } from 'react'
import EditRequestModal from '../EditRequestModal'
import { useEffect } from 'react'
import api from '../../services/api'
import RequestDeliveryImagesCarousel from '../RequestDeliveryImagesCarousel'

interface IRequest {
  id: number
  invoice: string
  request_number: string
  invoice_number: string
  receiver_name: string
  status: string
  cep: string
  city: string
  state: string
  neighborhood: string
  street: string
  street_number: string
  complement: string
  start_date: Date
  weight: number
  updated_at: string
  delivering_image_url: string
  delivering_image_key: string
  customer_rating_stars: number
  customer_rating_text: string
  request_delivery_driver_note: string
  request_delivery_time_seconds: number
  request_delivery_distance_meters: number
  vehicle_id: number
  vehicle_board: string
  delivery_id?: number
  driver_id: number
  driver_name: string
  delivery_had_problems: boolean
  note: string
  delivery_problem_solution_note: string
  is_internal_transfer: boolean
}

const status = {
  unallocated: { name: 'Não alocado', color: '#cca300' },
  allocated: { name: 'Alocado', color: '#00BFFF' },
  in_progress: { name: 'Em andamento', color: '#120a8f' },
  on_route: { name: 'Em rota', color: '#053C5E' },
  delivered: { name: 'Entregue', color: '#089c08' },
  canceled: { name: 'Cancelado', color: '#ff0000' },
}

const rolesMap = {
  manager: { api: 'delivery-manager', web: 'gestor-entregas' },
  attendant: { api: 'attendant', web: 'atendente' },
}

interface RequestModalProps {
  request: IRequest
  userRole: string
  showEditButton: boolean
  showRequestModal: boolean
  setShowRequestModal: (show: boolean) => void
  onRequestChange?: (updatedRequest: IRequest) => void
  onRequestDeleted?: (deletedRequestId: number) => void
}

const RequestModal: React.FC<RequestModalProps> = ({
  request,
  userRole,
  showEditButton,
  showRequestModal,
  setShowRequestModal,
  onRequestChange,
  onRequestDeleted,
}) => {
  const [showEditRequestModal, setShowEditRequestModal] = useState<boolean>(false)
  const [deliveryImages, setDeliveryImages] = useState<{ delivery_image_url: string }[]>([])

  const role = rolesMap[userRole as keyof typeof rolesMap]

  const navigate = useNavigate()

  const SecToMinSec = (sec: number) => {
    const min = Math.floor(sec / 60)
    const secs = sec - min * 60
    return `${min}min ${secs}s`
  }

  useEffect(() => {
    const shouldFetchDeliveryImages =
      showRequestModal &&
      request &&
      (request.status === 'delivered' ||
        (request.status === 'canceled' && hasRequestDeliveryData(request)))

    if (shouldFetchDeliveryImages) {
      api
        .get(`/${role.api}/requests/${request.id}/delivery-images`)
        .then((res) => {
          setDeliveryImages(res.data || [])
        })
        .catch(() => setDeliveryImages([]))
    } else {
      setDeliveryImages([])
    }
  }, [showRequestModal, request, role.api])

  const hasRequestDeliveryData = (request: IRequest) => {
    return (
      request.delivery_had_problems ||
      request.request_delivery_time_seconds ||
      request.request_delivery_distance_meters ||
      request.request_delivery_driver_note ||
      request.delivering_image_url
    )
  }

  return (
    <Modal centered size="lg" show={showRequestModal} onHide={() => setShowRequestModal(false)}>
      <Modal.Header closeButton />
      <Modal.Body style={{ marginLeft: 20, marginRight: 20 }}>
        <Row style={{ marginBottom: 1 }}>
          <p className={styles.basicHeader}>Dados básicos</p>
          {request.delivery_id && (
            <span style={{ marginBottom: 20 }}>
              <DeliveryFlow
                deliveryId={request.delivery_id}
                highlightedRequestId={request.id}
                role={userRole}
              />
            </span>
          )}
          <span className={styles.modalInfosSpan}>
            <ul style={{ padding: 0, margin: 0 }}>
              <li>
                <b>Endereço:</b> {request?.street}, {request?.street_number},{' '}
                {request?.neighborhood}
                {request?.complement && <span>, {request?.complement}</span>} - {request?.city} -{' '}
                {request?.state}
              </li>
              <li>
                <span>
                  <b>Destinatário:</b> {request?.receiver_name || 'Não informado'}
                </span>
              </li>
              <li>
                <span>
                  <b>Motorista:</b> {request?.driver_name || 'Não informado'}
                </span>
              </li>
              {userRole !== 'attendant' && (
                <li className={styles.invoiceLi}>
                  <b>Nota fiscal: </b>
                  {request?.invoice}
                </li>
              )}
              {request?.request_number && (
                <li>
                  <b>Número do pedido: </b>
                  {request?.request_number}
                </li>
              )}
              {request?.invoice_number && (
                <li>
                  <b>Número da nota fiscal: </b>
                  {request?.invoice_number}
                </li>
              )}
              {userRole !== 'attendant' && (
                <li>
                  <b>Peso:</b> {request?.weight} KG
                </li>
              )}
              <li className="d-flex align-items-center">
                <b>Status: </b>
                {request?.status && (
                  <span>
                    <Badge
                      bg="unset"
                      color="white"
                      pill
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: status[request.status as keyof typeof status].color,
                      }}
                    >
                      {status[request.status as keyof typeof status].name}
                    </Badge>
                  </span>
                )}
              </li>
              <li>
                <b>Transferência interna:</b>{' '}
                {request?.is_internal_transfer === true ? 'Sim' : 'Não'}
              </li>
              {request.note && (
                <li>
                  <b>Observação:</b> {request?.note}
                </li>
              )}
            </ul>
          </span>
        </Row>
        {(request?.status === 'on_route' || request?.status === 'delivered') && (
          <Row className="mb-4">
            <Button
              onClick={() =>
                navigate(
                  `/${rolesMap[userRole as keyof typeof rolesMap].web}/entregas/${request?.id}`,
                )
              }
            >
              <p style={{ fontSize: 14 }} className="m-0">
                Ver mais detalhes
              </p>
            </Button>
          </Row>
        )}
        {request && request.customer_rating_stars > 0 && (
          <Row>
            <hr></hr>
            <p className={styles.customerRatingHeader}> Avaliação do cliente</p>
            <span
              style={{
                display: 'block',
                fontWeight: '400',
                fontSize: '0.9rem',
              }}
            >
              <ul style={{ padding: 0, margin: 0 }}>
                <li>
                  <b>Nota:</b> {request.customer_rating_stars + ' de 5'}
                </li>
                <li>
                  <b>Observação:</b> {request.customer_rating_text || 'Não fornecida.'}
                </li>
              </ul>
            </span>
          </Row>
        )}
        {request &&
          (request.status === 'delivered' ||
            (request.status === 'canceled' && hasRequestDeliveryData(request))) && (
            <Row className="mt-4">
              <hr></hr>
              <p className={styles.customerRatingHeader}>Sobre a entrega realizada</p>
              <span className={styles.deliveredInfo}>
                <ul style={{ padding: 0, margin: 0 }}>
                  <li className="mt-2">
                    <b>Ocorreu algum problema na entrega? </b>{' '}
                    {request.delivery_had_problems ? 'Sim' : 'Não'}
                  </li>
                  <li>
                    <b>Tempo até a entrega:</b>{' '}
                    {SecToMinSec(request.request_delivery_time_seconds) || 'Desconhecido.'}
                  </li>
                  <li>
                    <b>Distância percorrida até a entrega:</b>{' '}
                    {metersToKms(request.request_delivery_distance_meters) || 'Desconhecida.'}
                  </li>
                  <li>
                    <b>Observação do motorista:</b>{' '}
                    {request.request_delivery_driver_note || 'Não fornecida.'}
                  </li>
                  {request.delivery_had_problems && (
                    <li>
                      <b>Solução do problema:</b>{' '}
                      {request.delivery_problem_solution_note || 'Ainda não informada.'}
                    </li>
                  )}
                  <li>
                    <b>Registro da entrega:</b>
                  </li>
                  {deliveryImages.length > 0 && (
                    <RequestDeliveryImagesCarousel images={deliveryImages} />
                  )}
                </ul>
              </span>
            </Row>
          )}
      </Modal.Body>
      <hr></hr>
      <Row className="text-center">
        {showEditButton && (
          <Col>
            <Button
              onClick={() => {
                setShowEditRequestModal(true)
              }}
              className={`${styles.modalBtn} ${styles.editBtn}`}
            >
              Editar
            </Button>
          </Col>
        )}
        <Col>
          <Button
            onClick={() => setShowRequestModal(false)}
            className={`${styles.modalBtn} ${styles.closeBtn}`}
          >
            Fechar
          </Button>
        </Col>
      </Row>
      <EditRequestModal
        request={request}
        show={showEditRequestModal}
        onClose={setShowEditRequestModal}
        role={role.api}
        onEdit={(updatedRequest: IRequest) => {
          if (onRequestChange) onRequestChange(updatedRequest)
        }}
        onDelete={(deletedRequestId: number) => {
          if (onRequestDeleted) onRequestDeleted(deletedRequestId)
        }}
      />
    </Modal>
  )
}

export default RequestModal
